import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { LocalStorageKeys, SessionStorageKeys } from "utils/storageKeys";
import { I18NextBackend } from "./backend";
import { applyMomentLocale } from "./dateLocale";
import { logWarning } from "services/logging";
import type { TranslationKey } from "./keys";

/**
 * Vouches for a key the union cannot prove — one assembled at runtime from a suffix
 * that lives in data rather than in the call. Prefer typing the data as
 * `TranslationKey` and passing it straight to `t()`; reach for this only where the
 * suffix genuinely is not known until render.
 */
export function dynamicKey(key: string): TranslationKey {
  return key as TranslationKey;
}

export function initI18n() {
  const language = resolveUiLanguage(getStoredUiLanguage(), getBrowserUiLanguage());

  // Duplicated in LocalizationProvider, so the first dates usually render localized without
  // blocking startup on a chunk.
  void applyMomentLocale(language);

  void i18n
    .use(new I18NextBackend())
    .use(initReactI18next)
    .init({
      lng: language,
      fallbackLng: [DEFAULT_UI_LANGUAGE],
      preload: [DEFAULT_UI_LANGUAGE],
      interpolation: {
        escapeValue: false, // react already protects from XSS here
      },
      backend: {
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
    });
}

export const UI_LANGUAGES = [
  { name: "English", value: "en" },
  { name: "Deutsch", value: "de" },
  { name: "Français", value: "fr" },
  { name: "Português", value: "pt" },
  { name: "Español", value: "es" },
  { name: "中文", value: "zh" },
] as const;

const UI_LANGUAGE_CODES = UI_LANGUAGES.map(lang => lang.value);

export type UiLanguage = (typeof UI_LANGUAGES)[number]["value"];

export const DEFAULT_UI_LANGUAGE: UiLanguage = "en";

export function parseUiLanguage(language?: string | null): UiLanguage | null {
  // Accepts raw language strings from external sources like backend payloads,
  // browser APIs, or storage, and normalizes them into canonical UI codes.
  if (!language) {
    return null;
  }

  const normalized = language.toLowerCase().split("-")[0];
  if (UI_LANGUAGE_CODES.includes(normalized as UiLanguage)) {
    return normalized as UiLanguage;
  } else {
    logWarning(`Unsupported UI language code: ${language}`);
    return null;
  }
}

export function getStoredUiLanguage(): UiLanguage | null {
  return parseUiLanguage(localStorage.getItem(LocalStorageKeys.language));
}

export function persistUiLanguage(language: UiLanguage): void {
  localStorage.setItem(LocalStorageKeys.language, language);
}

export function getBrowserUiLanguage(): UiLanguage | null {
  return parseUiLanguage(navigator.language);
}

export function resolveUiLanguage(
  ...languages: Array<UiLanguage | null | undefined>
): UiLanguage {
  // Works only with canonical UiLanguage values. Raw strings should be parsed
  // at the boundary before they reach this resolution step.
  return (
    languages.find(
      (language): language is UiLanguage => language !== null && language !== undefined
    ) ?? DEFAULT_UI_LANGUAGE
  );
}

export function resolveInitialUiLanguage(preferredLanguage?: string | null): UiLanguage {
  return resolveUiLanguage(
    parseUiLanguage(preferredLanguage),
    getStoredUiLanguage(),
    getBrowserUiLanguage()
  );
}

// These helpers are for the separate native-language translation flow used by
// conversation/dictionary features. They intentionally remain outside the UI
// localization state managed by LocalizationProvider.
export function saveTempNativeLanguage(langCode: string | undefined): void {
  sessionStorage.setItem(SessionStorageKeys.tempNativeLanguage, langCode ?? "");
}
export function getTempNativeLanguage(): string | null {
  return sessionStorage.getItem(SessionStorageKeys.tempNativeLanguage);
}

export default i18n;
